import React, { useRef, useState } from "react";
import Icon from "../Icon";
import {
  countdownLabel,
  daysUntil,
  DEFAULT_LEAD_BUSINESS_DAYS,
  formatFriendly,
  isoForBusinessDayOffset,
} from "../helpers";
import { FormattedUpload } from "../types";
import { useToast } from "../Toast";

// Recon runs in a fixed evening window, so picking a specific run moment no
// longer applies. Reschedule is hidden but kept intact for later re-enabling.
const ALLOW_RESCHEDULE = false;

interface Props {
  record: FormattedUpload;
  canEdit: boolean;
  canReschedule: boolean;
  canExpedite: boolean;
  onReplace: (record: FormattedUpload, file: File) => void;
  onReschedule: (record: FormattedUpload, newDate: string) => void;
  onCancel: (record: FormattedUpload) => void;
  onExpedite: (record: FormattedUpload) => void;
}

interface Option {
  key: string;
  label: string;
  sub: string;
  date: string;
  days: number;
  isDefault?: boolean;
}

const ScheduledManager: React.FC<Props> = ({
  record,
  canEdit,
  canReschedule,
  canExpedite,
  onReplace,
  onReschedule,
  onCancel,
  onExpedite,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [expediteRequested, setExpediteRequested] = useState(false);
  const toast = useToast();
  const countdown = countdownLabel(record.scheduledFor || "");
  const due = new Date(record.scheduledFor || "").getTime() <= Date.now();

  const handleFile = (file: File | null | undefined): void => {
    if (!file) return;
    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (!["xlsx", "xls"].includes(extension)) {
      toast.push({
        kind: "error",
        title: "That file type isn’t supported",
        desc: "Please upload an Excel file (.xlsx or .xls).",
      });
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.push({
        kind: "error",
        title: "File is too big",
        desc: "The largest file we can take is 25 MB.",
      });
      return;
    }
    onReplace(record, file);
  };

  const pickReplacement = (): void => fileInputRef.current?.click();

  const today1700 = isoForBusinessDayOffset(0, 17, 0);
  const in2bd0900 = isoForBusinessDayOffset(2, 9, 0);
  const in4bd0900 = isoForBusinessDayOffset(4, 9, 0);
  const options: Option[] = [
    {
      key: "today",
      label: "Today",
      sub: "At 17:00 GMT",
      date: today1700,
      days: 0,
    },
    {
      key: "2bd",
      label: "In 2 working days",
      sub: formatFriendly(in2bd0900),
      date: in2bd0900,
      days: 2,
    },
    {
      key: "4bd",
      label: "In 4 working days",
      sub: formatFriendly(in4bd0900),
      date: in4bd0900,
      days: 4,
    },
    {
      key: "default",
      label: "In 5 working days",
      sub: "Default",
      date: record.originalScheduledFor || record.scheduledFor || "",
      days: DEFAULT_LEAD_BUSINESS_DAYS,
      isDefault: true,
    },
  ];

  return (
    <div className="sched-card">
      <div className="sched-card-head">
        <div className="sched-card-icon">
          <Icon name="clock" size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sched-card-title">
            Reconciles on {formatFriendly(record.scheduledFor)}
            <span
              className={`countdown ${countdown.cls}`}
              style={{ marginLeft: 8, verticalAlign: "2px" }}
            >
              {countdown.text}
            </span>
          </div>
          <div className="sched-card-sub">
            {due ? (
              "Starting shortly — it’s queued for the current run window."
            ) : (
              <>
                We’ll run the reconciliation automatically at 7:00 PM in the
                evening window. Until then, you can swap the file
                {ALLOW_RESCHEDULE && canReschedule
                  ? " or bring the date forward"
                  : ""}
                .
              </>
            )}
          </div>
          {record.rescheduledAt && (
            <div
              className="sched-card-sub"
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: "1px dashed rgba(29,109,255,0.2)",
              }}
            >
              <Icon
                name="info"
                size={12}
                style={{ verticalAlign: "-2px", marginRight: 4 }}
              />{" "}
              Moved earlier by <strong>{record.rescheduledBy?.name}</strong> on{" "}
              {formatFriendly(record.rescheduledAt, true)} (was originally{" "}
              {formatFriendly(record.originalScheduledFor)}).
              {record.rescheduleReason && (
                <>
                  {" "}
                  — <em>{record.rescheduleReason}</em>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {canEdit && (
        <>
          <div className="sched-actions">
            <button className="btn" onClick={pickReplacement}>
              <Icon name="upload" size={13} /> Replace file…
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={(event) => handleFile(event.target.files?.[0])}
            />
            {ALLOW_RESCHEDULE && (
              <button
                className="btn"
                onClick={() => setShowReschedule((open) => !open)}
                disabled={!canReschedule}
                title={canReschedule ? "" : "Only senior analysts can do this"}
              >
                <Icon name="calendar" size={13} />{" "}
                {canReschedule
                  ? "Change run date…"
                  : "Change run date (not allowed)"}
              </button>
            )}
            {canExpedite && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setExpediteRequested(true);
                  onExpedite(record);
                }}
                disabled={expediteRequested}
                title={
                  expediteRequested
                    ? "Already starting — refresh to see the new status"
                    : "Skip the scheduled wait and run this now"
                }
              >
                <Icon name="zap" size={13} />{" "}
                {expediteRequested ? "Starting…" : "Start now"}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-danger-ghost"
              onClick={() => onCancel(record)}
            >
              <Icon name="x" size={13} strokeWidth={2.5} /> Cancel this
              reconciliation
            </button>
          </div>

          {ALLOW_RESCHEDULE && !canReschedule && (
            <div className="restricted-note">
              <Icon name="shield" size={12} /> Only senior analysts can change
              the run date. You can still replace the file.
            </div>
          )}

          {ALLOW_RESCHEDULE && showReschedule && canReschedule && (
            <div className="reschedule-panel">
              <div
                style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}
              >
                Pick a new date
              </div>
              <div
                style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}
              >
                Move the date anywhere within the original 5-working-day
                correction window — pull forward if TA has confirmed early, or
                push back if they need more time. Can&apos;t extend past the
                original run date.
              </div>
              <div className="reschedule-options">
                {options.map((option) => {
                  const isCurrent = option.date === record.scheduledFor;
                  // Allow rescheduling in any direction within the original
                  // window. Only disable if the option is the current value or
                  // is in the past (today already passed).
                  const isInPast = daysUntil(option.date) < 0;
                  const original =
                    record.originalScheduledFor || record.scheduledFor || "";
                  const isBeyondOriginal =
                    !!original && daysUntil(option.date) > daysUntil(original);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`reschedule-opt ${
                        isCurrent ? "is-current" : ""
                      }`}
                      disabled={isCurrent || isInPast || isBeyondOriginal}
                      onClick={() => {
                        onReschedule(record, option.date);
                        setShowReschedule(false);
                      }}
                    >
                      <strong>{option.label}</strong>
                      <span>{formatFriendly(option.date, true)}</span>
                      {option.isDefault && (
                        <span className="badge">Original</span>
                      )}
                      {isCurrent && <span className="badge">Current</span>}
                    </button>
                  );
                })}
              </div>
              <div className="reschedule-note">
                <Icon name="alert-triangle" size={12} /> Once reconciliation
                starts, the file is locked — you won’t be able to swap it.
              </div>
              <div className="reschedule-foot">
                <button
                  className="btn btn-sm"
                  onClick={() => setShowReschedule(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {!canEdit && (
        <div className="restricted-note">
          <Icon name="shield" size={12} /> Only authorized analysts can swap the
          file or change this date.
        </div>
      )}
    </div>
  );
};

export default ScheduledManager;
